import { Injectable } from '@angular/core';
import { MachineApiService } from './machine-api.service';

type ApiResponse<T> = { data: T; success: boolean };

export interface QuotationTemplate {
  id: string;
  name: string;
  placeholderKeys: string[];
  createdAt: string;
  updatedAt: string;
}

export interface QuotationRecord {
  id: string;
  templateId?: string;
  templateName?: string;
  generatedPresentationId?: string;
  generatedFileId?: string;
  generatedName: string;
  status: 'draft' | 'ready_to_send' | 'sent' | 'accepted' | 'rejected' | 'expired';
  placeholders: Record<string, string>;
  recipientEmail?: string;
  sentAt?: string;
  lastSentSubject?: string;
  lastSentBody?: string;
  unmatchedPlaceholders: string[];
  missingKeys: string[];
  validationWarnings: string[];
  webViewLink?: string;
  previewUrl?: string;
  pdfUrl?: string;
  renderStatus?: 'idle' | 'html_ready' | 'pdf_ready' | 'failed';
  artifacts?: {
    html?: {
      key: string;
      contentType: string;
      sizeBytes: number;
      etag?: string;
      version: number;
      generatedAt: string;
    };
    pdf?: {
      key: string;
      contentType: string;
      sizeBytes: number;
      etag?: string;
      version: number;
      generatedAt: string;
    };
  };
  createdAt: string;
  updatedAt: string;
}

@Injectable({ providedIn: 'root' })
export class QuotationsService {
  constructor(private readonly machineApi: MachineApiService) {}

  private async unwrap<T>(promise: Promise<ApiResponse<T>>): Promise<T> {
    const res = await promise;
    return res.data;
  }

  private isTransientNetworkError(err: unknown): boolean {
    const message = String((err as any)?.message ?? '').toLowerCase();
    return (
      message.includes('failed to fetch') ||
      message.includes('networkerror') ||
      message.includes('load failed') ||
      message.includes('network request failed')
    );
  }

  private withProjectId(path: string, projectId: string): string {
    const normalized = String(projectId ?? '').trim();
    const separator = path.includes('?') ? '&' : '?';
    return `${path}${separator}projectId=${encodeURIComponent(normalized)}`;
  }

  async generateQuotation(projectId: string, input: {
    title?: string;
    placeholders?: Record<string, string>;
    recipientEmail?: string;
  }): Promise<QuotationRecord> {
    const result = await this.unwrap(
      this.machineApi.postJson<ApiResponse<{ quotation: QuotationRecord }>>(
        this.withProjectId('/api/quotations/generate', projectId),
        input
      )
    );
    return result.quotation;
  }

  async listQuotations(projectId: string): Promise<QuotationRecord[]> {
    const result = await this.unwrap(
      this.machineApi.request<ApiResponse<{ quotations: QuotationRecord[] }>>(
        this.withProjectId('/api/quotations', projectId)
      )
    );
    return Array.isArray(result?.quotations) ? result.quotations : [];
  }

  async updateQuotation(projectId: string, id: string, input: {
    generatedName?: string;
    placeholders?: Record<string, string>;
    recipientEmail?: string | null;
  }): Promise<QuotationRecord> {
    const request = () =>
      this.unwrap(
        this.machineApi.request<ApiResponse<{ quotation: QuotationRecord }>>(
          this.withProjectId(`/api/quotations/${encodeURIComponent(id)}`, projectId),
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(input ?? {}),
          }
        )
      );
    try {
      const result = await request();
      return result.quotation;
    } catch (err: unknown) {
      if (!this.isTransientNetworkError(err)) throw err;
      await new Promise((resolve) => setTimeout(resolve, 300));
      const result = await request();
      return result.quotation;
    }
  }

  async updateQuotationStatus(
    projectId: string,
    id: string,
    status: QuotationRecord['status']
  ): Promise<QuotationRecord> {
    const result = await this.unwrap(
      this.machineApi.postJson<ApiResponse<{ quotation: QuotationRecord }>>(
        this.withProjectId(`/api/quotations/${encodeURIComponent(id)}/status`, projectId),
        { status }
      )
    );
    return result.quotation;
  }

  async sendQuotationEmail(
    projectId: string,
    id: string,
    input: { to: string; subject?: string; message?: string }
  ): Promise<QuotationRecord> {
    const result = await this.unwrap(
      this.machineApi.postJson<ApiResponse<{ quotation: QuotationRecord }>>(
        this.withProjectId(`/api/quotations/${encodeURIComponent(id)}/send-email`, projectId),
        input
      )
    );
    return result.quotation;
  }

  async refreshQuotation(projectId: string, id: string): Promise<QuotationRecord> {
    const result = await this.unwrap(
      this.machineApi.postJson<ApiResponse<{ quotation: QuotationRecord }>>(
        this.withProjectId(`/api/quotations/${encodeURIComponent(id)}/refresh`, projectId),
        {}
      )
    );
    return result.quotation;
  }

  async deleteQuotation(projectId: string, id: string): Promise<void> {
    await this.unwrap(
      this.machineApi.request<ApiResponse<{ deleted: boolean }>>(
        this.withProjectId(`/api/quotations/${encodeURIComponent(id)}`, projectId),
        {
          method: 'DELETE',
        }
      )
    );
  }
}
